import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { FieldsWithType } from '@lmt/unicorn-shared/app/helpers/typeUtils';
import clsx from 'clsx';
import * as React from 'react';
import { useCallback } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { Control, FieldPath } from 'react-hook-form/dist/types';
import { RegisterOptions } from 'react-hook-form/dist/types/validator';
import { CheckBox } from './CheckBox';

import styles from './CheckboxGroup.module.scss';
import { TagsEditor } from './tags/TagsEditor';
import { OnTagsChangeEvent } from './tags/tags-helpers';

type Props<
  D extends Record<V, string | number>,
  V extends FieldsWithType<D, string | number>,
> = {
  options: D[];
  valueField: V;
  labelField: FieldsWithType<D, string>;
  disabledField?: (item: D) => boolean;
  selectedValues: D[V][];
  rootClassName?: string;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  editableFieldValue?: D[V];
  onEditableFieldChange?: (value: string) => void;
  onSelectedOptionChanged: (newSelectedOption: D[]) => void;
  checkboxClassName?: string;
  isMultiline?: boolean;
  visibleOptionsHeight?: number;
};

export function CheckboxGroup<
  D extends Record<V, string | number>,
  V extends FieldsWithType<D, string | number>,
>(props: Props<D, V>) {
  const {
    rootClassName,
    options,
    valueField,
    labelField,
    disabled,
    onSelectedOptionChanged,
    selectedValues,
    error,
    errorText,
    editableFieldValue,
    onEditableFieldChange,
    checkboxClassName,
    isMultiline,
    visibleOptionsHeight,
  } = props;
  const getLabelForOption = useCallback((option: D) => option[labelField], []);

  const getValueForOption = useCallback(
    (option: D) => option && option[valueField],
    [],
  );

  const onChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>, selectedOption: D) => {
      const foundOption = selectedOption;

      if (foundOption) {
        if (e.currentTarget.checked) {
          const updatedOptions = options.filter(
            (option) =>
              selectedValues.includes(getValueForOption(option)) ||
              option === foundOption,
          );
          onSelectedOptionChanged(updatedOptions);
        } else {
          const updatedOptions = options.filter(
            (option) =>
              selectedValues.includes(getValueForOption(option)) &&
              option !== foundOption,
          );
          onSelectedOptionChanged(updatedOptions);
        }
      } else {
        const updatedOptions = options.filter((option) =>
          selectedValues.includes(option[valueField]),
        );
        onSelectedOptionChanged(updatedOptions);
      }
    },
    [options, onSelectedOptionChanged, selectedValues],
  );
  const editableChange = useCallback(
    (e: OnTagsChangeEvent, value: string) => {
      if (value === undefined) return;

      // select the editable option when user starts to edit it
      if (value && props.editableFieldValue) {
        if (!selectedValues.includes(props.editableFieldValue)) {
          selectedValues.push(props.editableFieldValue);
          const updatedOptions = options.filter((option) => {
            if (option[valueField] === props.editableFieldValue) {
              option[labelField] = value as any;
            }
            return selectedValues.includes(option[valueField]);
          });
          onEditableFieldChange?.(value);
          onSelectedOptionChanged(updatedOptions);
          return;
        }
      }
      onEditableFieldChange?.(value);
    },
    [onEditableFieldChange, selectedValues, props.editableFieldValue, options],
  );
  return (
    <>
      <div
        className={clsx(styles.rootContainer, rootClassName)}
        style={{
          maxHeight: visibleOptionsHeight,
        }}
        data-test-id={'checkbox-group'}
      >
        {options.map((option) => {
          const value = getValueForOption(option);
          const label = getLabelForOption(option)?.toString();
          return (
            <div key={value} className={styles.fieldContainer}>
              <CheckBox
                title={value === editableFieldValue ? '' : label}
                key={value}
                checked={selectedValues?.includes(value)}
                name={createId() + value}
                disabled={disabled || (value === editableFieldValue && !label)} // CheckBox is disabled when option is empty
                onChange={(e) => {
                  onChange(e, option);
                }}
                className={clsx(styles.checkbox, checkboxClassName)}
              />
              {value === editableFieldValue && (
                <TagsEditor
                  value={label}
                  onChange={editableChange}
                  isMultiline={!!isMultiline}
                />
              )}
            </div>
          );
        })}
      </div>
      {error && !!errorText && (
        <div data-error="true" className={styles.errorText}>
          {errorText}
        </div>
      )}
    </>
  );
}

type HookFormProps<
  D extends FieldValues & Record<V, string | number>,
  V extends FieldsWithType<D, string | number>,
  TFieldValues extends FieldValues = FieldValues,
> = Omit<Props<D, V>, 'onSelectedOptionChanged'> & {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: Exclude<RegisterOptions, 'valueAsDate' | 'setValueAs'>;
  onFocus?: () => void;
  defaultValue?: unknown;
};

export function HookFormCheckboxGroup<
  D extends FieldValues & Record<V, string | number>,
  V extends FieldsWithType<D, string | number>,
  TFieldValues extends FieldValues = FieldValues,
>(props: HookFormProps<D, V, TFieldValues>) {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      render={({ field }) => (
        <CheckboxGroup
          {...props}
          selectedValues={field.value as any}
          onSelectedOptionChanged={(value: any) => {
            field.onChange(
              value
                ? props.valueField
                  ? value[props.valueField]
                  : value
                : null,
            );
          }}
        />
      )}
    />
  );
}
